import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {
  reduxForm,
  Field,
  submit,
  InjectedFormProps,
  formValueSelector
} from 'redux-form';

import ErrorBlock from 'components/ErrorBlock';
import Warning from 'components/Warning';
import Button from 'components/Button';
import FormField from 'components/FormField';
import UppercaseText from 'components/UppercaseText';
import {PartialTenantInfo} from 'components/TenantInfo';
import {SelectField, SearchField, RadioField} from 'components/Fields';

import {
  MODALS,
  filterGoCardlessCustomers,
  filterGoCardlessActiveMandates,
  fetchLinkGoCardlessMandateToBookingWarnings,
  connectGoCardlessCustomer,
  fetchGocardlessCustomerAndInvitation
} from 'ducks/tenancy';
import {noOpAction} from 'ducks';

import {selectModal, storeDataInModal, closeModal} from 'ducks/modals';

import {headOption} from 'utils/common';

import './styles.css';

const GOCARDLESS_CUSTOMER_CONNECT = 'goCardlessCustomerConnect';

const constructGoCardlessMandateOption = mandate => ({
  label: `${mandate.reference} - ${mandate.mandateId}`,
  value: mandate.mandateId
});

const constructGoCardlessCustomerOption = customer => ({
  label: `${customer.name} - ${customer.email}`,
  value: customer.id,
  object: customer
});

interface TenantProps {
  id: number;
  fullName: string;
  email: string;
}

interface GoCardlessMandate {
  mandateId: string;
  reference: string;
  paymentsRequireApproval: boolean;
}

interface GoCardlessMandateOption {
  label: string;
  value: string;
}

interface GoCardlessCustomer {
  email: string;
  id: string;
  name: string;
}

interface GoCardlessCustomerOption {
  label: string;
  value: string;
  object: GoCardlessCustomer;
}

interface GoCardlessSearchFormProps {
  locationRef: string;
  storeDataExtraActions: Function;
  filterGoCardlessCustomers: Function;
  filterGoCardlessActiveMandates: Function;
  loadingGoCardlessCustomers: boolean;
  goCardlessCustomers: Array<GoCardlessCustomer>;
  goCardlessActiveMandates: Array<GoCardlessMandate>;
  dispatch: Function;
  goCardlessCustomerValue: GoCardlessCustomerOption;
  goCardlessMandateValue: GoCardlessMandateOption;
  fetchLinkGoCardlessMandateToBookingWarnings: Function;
  connectWarnings: Array<string>;
  tenantsPerBooking: {
    primaryTenant: TenantProps;
    secondaryTenant?: TenantProps;
  };
  resetSection: Function;
  error: Array<string>;
}

const formSubmit = (values, dispatch, props) => {
  const extraActions = [
    () => closeModal(MODALS.SETUP_GOCARDLESS),
    () => fetchGocardlessCustomerAndInvitation({tenancyId: props.tenancyId})
  ];
  const data = {
    goCardlessCustomer: _.get(values, 'goCardlessCustomer.object'),
    goCardlessMandate: _.get(values, 'goCardlessMandate.value'),
    tenant: values.tenant,
    tenancyId: props.tenancyId
  };

  dispatch(connectGoCardlessCustomer(data, extraActions));
};

class GoCardlessSearchForm extends React.Component<
  GoCardlessSearchFormProps & InjectedFormProps
> {
  state = {
    goCardlessCustomerQuery: '',
    tenant: null
  };

  getGoCardlessCustomers = () => {
    const {locationRef} = this.props;
    const extraActions = [
      goCardlessCustomers =>
        storeDataInModal({
          modalName: MODALS.SETUP_GOCARDLESS,
          data: {goCardlessCustomers}
        })
    ];

    this.props.filterGoCardlessCustomers(
      {
        locationRef,
        query: this.state.goCardlessCustomerQuery
      },
      extraActions
    );
  };

  searchGoCardlessCustomers = event => {
    // event.keyCode = 13 -> ENTER
    if (event.keyCode === 13) {
      event.preventDefault();

      this.getGoCardlessCustomers();
    }
  };

  storeGoCardlessCustomerQuery = value => {
    this.setState({goCardlessCustomerQuery: value});
  };

  getWarningsExtraActions = () => [
    data =>
      storeDataInModal({
        modalName: MODALS.SETUP_GOCARDLESS,
        data: {
          connectWarnings: data.warnings
        }
      })
  ];

  clearTenantField = () => this.props.resetSection('tenant');

  clearMandateOptionsAndTenantField = () => {
    this.clearTenantField();
    this.props.dispatch(
      storeDataInModal({
        modalName: MODALS.SETUP_GOCARDLESS,
        data: {
          goCardlessActiveMandates: []
        }
      })
    );
  };

  onGoCardlessCustomerChange = selectedCustomer => {
    const customerId = _.get(selectedCustomer, 'value');

    // Clear all the fetched mandates and selected tenant when clearing the customer field
    if (_.isNil(customerId)) {
      return this.clearMandateOptionsAndTenantField();
    }

    const {locationRef} = this.props;

    const extraActions = [
      data =>
        storeDataInModal({
          modalName: MODALS.SETUP_GOCARDLESS,
          data: {
            goCardlessActiveMandates: data
          }
        }),
      data => {
        if (_.isEmpty(data)) {
          // We don't want to do a fetch warnings call if there are no active mandates.
          return noOpAction();
        }

        return fetchLinkGoCardlessMandateToBookingWarnings(
          {
            gocardlessCustomerId: customerId,
            gocardlessMandateId: _.get(data, '[0].mandateId')
          },
          this.getWarningsExtraActions()
        );
      }
    ];

    this.props.filterGoCardlessActiveMandates(
      {
        locationRef,
        customerId
      },
      extraActions
    );
  };

  onGoCardlessMandateChange = selectedMandate => {
    const mandateId = _.get(selectedMandate, 'value');

    if (_.isNil(mandateId)) {
      return this.clearTenantField();
    }

    this.fetchGoCardlessConnectWarning(selectedMandate);
  };

  fetchGoCardlessConnectWarning = selectedMandate => {
    const {goCardlessCustomerValue} = this.props;
    const customerId = _.get(goCardlessCustomerValue, 'value');
    const mandateId = _.get(selectedMandate, 'value');

    const extraActions = this.getWarningsExtraActions();

    if (!_.isNil(mandateId) && !_.isNil(customerId)) {
      this.props.fetchLinkGoCardlessMandateToBookingWarnings(
        {
          gocardlessCustomerId: customerId,
          gocardlessMandateId: mandateId
        },
        extraActions
      );
    }
  };

  triggerSubmit = () => {
    this.props.dispatch(submit(GOCARDLESS_CUSTOMER_CONNECT));
  };

  onTenantChange = event => {
    this.setState({tenant: parseInt(event.target.value)});
  };

  render() {
    const {
      loadingGoCardlessCustomers,
      goCardlessCustomers,
      goCardlessActiveMandates,
      submitting,
      goCardlessCustomerValue,
      goCardlessMandateValue,
      connectWarnings,
      tenantsPerBooking,
      error
    } = this.props;
    const {tenant} = this.state;
    const disableGoCardlessMandate =
      _.isEmpty(goCardlessActiveMandates) || _.isNil(goCardlessCustomerValue);

    return (
      <div className="GoCardlessSearch">
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <div className="content">
          <FormField>
            <UppercaseText>GOCARDLESS CUSTOMER</UppercaseText>
            <Field
              name="goCardlessCustomer"
              placeholder="Search for GoCardless Customer"
              component={SearchField}
              options={goCardlessCustomers.map(
                constructGoCardlessCustomerOption
              )}
              onInputKeyDown={this.searchGoCardlessCustomers}
              isLoading={loadingGoCardlessCustomers}
              onInputChange={this.storeGoCardlessCustomerQuery}
              onChange={this.onGoCardlessCustomerChange}
              filterOptions={false}
            />
          </FormField>

          <FormField>
            <UppercaseText>GOCARDLESS MANDATE</UppercaseText>
            <Field
              name="goCardlessMandate"
              placeholder="Select GoCardless Mandate"
              component={SelectField}
              options={goCardlessActiveMandates.map(
                constructGoCardlessMandateOption
              )}
              disabled={disableGoCardlessMandate}
              onChange={this.onGoCardlessMandateChange}
            />
          </FormField>

          {connectWarnings &&
            goCardlessMandateValue &&
            goCardlessCustomerValue &&
            connectWarnings.map(warning => (
              <Warning key={warning}>{warning}</Warning>
            ))}

          {tenantsPerBooking &&
            goCardlessMandateValue &&
            goCardlessCustomerValue && (
              <FormField>
                <UppercaseText>SELECT THE RELATED COLAB CUSTOMER</UppercaseText>

                <div className="radioButtonWrapper colab-centered-v">
                  <Field
                    name="tenant"
                    component={RadioField}
                    optionValue={tenantsPerBooking.primaryTenant.id}
                    checked={tenant === tenantsPerBooking.primaryTenant.id}
                    onChange={this.onTenantChange}
                  />

                  <PartialTenantInfo tenant={tenantsPerBooking.primaryTenant} />
                </div>

                {tenantsPerBooking.secondaryTenant && (
                  <div className="radioButtonWrapper colab-centered-v">
                    <Field
                      name="tenant"
                      component={RadioField}
                      optionValue={tenantsPerBooking.secondaryTenant.id}
                      checked={tenant === tenantsPerBooking.secondaryTenant.id}
                      onChange={this.onTenantChange}
                    />
                    <PartialTenantInfo
                      tenant={tenantsPerBooking.secondaryTenant}
                    />
                  </div>
                )}
              </FormField>
            )}
        </div>

        <div className="actionButton">
          <Button
            onClick={this.triggerSubmit}
            blue={!submitting}
            isLoading={submitting}
            disabled={
              submitting ||
              _.isNil(goCardlessMandateValue) ||
              _.isNil(goCardlessCustomerValue) ||
              _.isNil(tenant)
            }>
            Connect
          </Button>
        </div>
      </div>
    );
  }
}

const formSelector = formValueSelector(GOCARDLESS_CUSTOMER_CONNECT);

const mapStateToProps = state => {
  const {
    data: {
      locationRef,
      tenantsPerBooking,
      goCardlessCustomers,
      goCardlessActiveMandates,
      loadingGoCardlessCustomers,
      connectWarnings
    }
  } = selectModal(state, MODALS.SETUP_GOCARDLESS);

  const goCardlessMandate = headOption(
    goCardlessActiveMandates,
    constructGoCardlessMandateOption
  );

  return {
    goCardlessCustomers: goCardlessCustomers || [],
    goCardlessActiveMandates: goCardlessActiveMandates || [],
    loadingGoCardlessCustomers,
    locationRef,
    connectWarnings: connectWarnings || [],
    tenantsPerBooking,
    initialValues: {
      goCardlessMandate
    },
    goCardlessCustomerValue: formSelector(state, 'goCardlessCustomer'),
    goCardlessMandateValue: formSelector(state, 'goCardlessMandate')
  };
};

const mapDispatchToProps = {
  filterGoCardlessCustomers,
  filterGoCardlessActiveMandates,
  fetchLinkGoCardlessMandateToBookingWarnings
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: GOCARDLESS_CUSTOMER_CONNECT,
    onSubmit: formSubmit,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    updateUnregisteredFields: true
  })(GoCardlessSearchForm)
);
